/**

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

import type { ReactNode } from "react";

// @mui material components
import Badge from "@mui/material/Badge";
import { styled } from "@mui/material/styles";
import type { CSSObject } from "@mui/material/styles";

interface OwnerState {
  color:
    | "primary"
    | "secondary"
    | "info"
    | "text"
    | "success"
    | "warning"
    | "error"
    | "light"
    | "dark";
  variant: "gradient" | "contained" | "basic";
  size: "xs" | "sm" | "md" | "lg";
  circular: boolean;
  indicator: boolean;
  border: boolean;
  container: boolean;
  children: ReactNode;
}

export default styled(Badge)<{ ownerState: OwnerState }>(({ theme, ownerState }) => {
  const { palette, typography, borders, functions } = theme;
  const { color, circular, border, size, indicator, variant, container, children } = ownerState;

  const { white, dark, gradients, badgeColors } = palette;
  const { size: fontSize, fontWeightBold } = typography;
  const { borderRadius, borderWidth } = borders;
  const { pxToRem, linearGradient } = functions;

  // Not every badge color has a gradient or badge color (`text` has neither).
  const gradientsByColor: Partial<Record<string, { main: string; state: string }>> = gradients;
  const badgeColorsByColor: Partial<Record<string, { basic: string; background: string; text: string }>> =
    badgeColors;

  // padding values
  const paddings = {
    xs: "0.575em 0.775em 0.4em",
    sm: "0.55em 0.9em",
    md: "0.75em 1em 0.65em",
    lg: "0.65em 1.375em 0.65em",
  };

  // fontSize value
  const fontSizeValue = size === "xs" ? fontSize.xxs : fontSize.xs;

  // border value
  const borderValue = border ? `${borderWidth[1]} solid ${white.main}` : "none";

  // borderRadius value
  let borderRadiusValue = size === "xs" ? borderRadius.md : borderRadius.badge;

  if (circular) {
    borderRadiusValue = borderRadius.section;
  }

  // styles for the badge with indicator={true}
  const indicatorStyles = (sizeProp: string): CSSObject => {
    let widthValue = pxToRem(20);
    let heightValue = pxToRem(20);

    if (sizeProp === "medium") {
      widthValue = pxToRem(24);
      heightValue = pxToRem(24);
    } else if (sizeProp === "large") {
      widthValue = pxToRem(32);
      heightValue = pxToRem(32);
    }

    return {
      width: widthValue,
      height: heightValue,
      display: "grid",
      placeItems: "center",
      textAlign: "center",
      borderRadius: "50%",
      padding: 0,
      border: borderValue,
    };
  };

  // styles for the badge with variant="gradient"
  const gradientStyles = (colorProp: string) => {
    const gradient = gradientsByColor[colorProp];
    const backgroundValue = gradient
      ? linearGradient(gradient.main, gradient.state)
      : linearGradient(gradients.info.main, gradients.info.state);
    const colorValue = colorProp === "light" ? dark.main : white.main;

    return {
      background: backgroundValue,
      color: colorValue,
    };
  };

  // styles for the badge with variant="contained"
  const containedStyles = (colorProp: string) => {
    const badgeColor = badgeColorsByColor[colorProp];
    const backgroundValue = badgeColor
      ? badgeColor.background
      : badgeColors.info.background;
    let colorValue = badgeColor ? badgeColor.text : badgeColors.info.text;

    if (colorProp === "light") {
      colorValue = dark.main;
    }
    return {
      background: backgroundValue,
      color: colorValue,
    };
  };

  const basicStyles = (colorProp: string) => {
    let colorValue = white.main;

    const badgeColor = badgeColorsByColor[colorProp];
    const backgroundValue = badgeColor
      ? badgeColor.basic
      : badgeColors.info.basic;
    if (colorProp === "light") {
      colorValue = dark.main;
    }
    return {
      background: backgroundValue,
      color: colorValue,
    };
  };

  // styles for the badge with no children and container={false}
  const standAloneStyles = (): CSSObject => ({
    position: "static",
    marginLeft: pxToRem(8),
    transform: "none",
    fontSize: pxToRem(10),
  });

  // styles for the badge with container={true}
  const containerStyles = (): CSSObject => ({
    position: "relative",
    transform: "none",
  });

  return {
    "& .MuiBadge-badge": {
      height: "auto",
      padding: paddings[size] || paddings.xs,
      fontSize: fontSizeValue,
      fontWeight: fontWeightBold,
      lineHeight: 1,
      textAlign: "center",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      verticalAlign: "baseline",
      border: borderValue,
      borderRadius: borderRadiusValue,
      ...(indicator && indicatorStyles(size)),
      ...(variant === "gradient" && gradientStyles(color)),
      ...(variant === "contained" && containedStyles(color)),
      ...(variant === "basic" && basicStyles(color)),
      ...(!children && !container && standAloneStyles()),
      ...(container && containerStyles()),
    },
  };
});
